/**
 * Where a signed-in user lands, in one place.
 *
 * This used to be five hardcoded `/quotes` literals that had to agree with each other —
 * LoginPage's default, App's `/` redirect, the authenticated bounces off `/login` and
 * `/forgot-password`, and the `/change-password` fallback. Changing the landing page meant
 * finding all five; missing one left the app sending people to two different screens
 * depending on how they arrived.
 */

import { readGuarded, writeGuarded } from './guardedStorage';
import { moduleForPath } from '../modules/registry';

/** Used whenever there is no better answer. Also the fallback for a bad stored value. */
export const DEFAULT_LANDING_ROUTE = '/quotes';

const KEY_PREFIX = 'crm_last_route_';

/**
 * Per USER, not per browser. A shared terminal is normal on a shop floor, and landing on
 * the previous person's last screen would be both confusing and a small information leak.
 */
const keyFor = (userId) => `${KEY_PREFIX}${userId}`;

/**
 * Only in-app routes, and never a route that would bounce straight back out again.
 * Anything else falls back to the default rather than being trusted.
 */
const isStorableRoute = (value) =>
  typeof value === 'string' &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !['/login', '/forgot-password', '/change-password', '/logout', '/'].includes(value);

/**
 * A record or builder URL inside a module that routes itself is remembered as the module, never
 * the record: the next sign-in must not land someone inside a builder (pilot decision 6). The
 * modules are the registry's (G0); Quotes is the first.
 */
export const collapseModuleRoute = (pathname, modules) => {
  const owner = modules ? moduleForPath(pathname, modules) : moduleForPath(pathname);
  return owner ? owner.routeRoot : pathname;
};

/** Remember where this user was, so their next sign-in returns them there. */
export const rememberLastRoute = (userId, pathname) => {
  if (!userId || !isStorableRoute(pathname)) return;
  writeGuarded(keyFor(userId), collapseModuleRoute(pathname));
};

/**
 * Where to send this user after sign-in. `deepLinkFrom` — the protected route they were
 * bounced off — always wins: they asked for that page seconds ago, which is a better
 * signal than where they were last time.
 */
export const landingRouteFor = (userId, deepLinkFrom) => {
  if (isStorableRoute(deepLinkFrom)) return deepLinkFrom;
  if (!userId) return DEFAULT_LANDING_ROUTE;
  const stored = readGuarded(keyFor(userId), isStorableRoute, DEFAULT_LANDING_ROUTE);
  return isStorableRoute(stored) ? stored : DEFAULT_LANDING_ROUTE;
};
